<!--
  Angular 15: *ngIf / *ngFor only, no @if blocks.
-->
<div class="dl-page">

  <!-- Said "Back to Partners" on a case page. It goes back, wherever from. -->
  <button type="button" class="dl-page__back" (click)="goBack()">
    <mat-icon>chevron_left</mat-icon>
    Back
  </button>

  <div class="dl-page__header">
    <div class="dl-page__heading">
      <h1 class="dl-page__title" *ngIf="case">Case #{{ case.id }}</h1>
      <h1 class="dl-page__title" *ngIf="!case && !loading">Case details</h1>
    </div>
    <div class="dl-page__actions" *ngIf="case && !loading && !error">
      <button type="button" class="dl-btn dl-btn--danger" (click)="askDelete()">
        <mat-icon>delete</mat-icon>
        Delete case
      </button>
    </div>
  </div>

  <div *ngIf="loading" class="dl-loading">
    <div class="dl-spinner"></div>
    <p>Loading case details…</p>
  </div>

  <div *ngIf="error" class="dl-banner dl-banner--error">
    <mat-icon>error</mat-icon>
    <div class="dl-banner__body">{{ error }}</div>
    <div class="dl-banner__actions">
      <button type="button" class="dl-btn dl-btn--sm" (click)="goBack()">Back</button>
    </div>
  </div>

  <ng-container *ngIf="case && !loading && !error">

    <div class="dl-card">
      <div class="dl-card__header">
        <h2 class="dl-card__title">Case information</h2>
      </div>
      <div class="dl-card__body">
        <div class="dl-kv">
          <div class="dl-kv__item">
            <span class="dl-kv__key">Case ID</span>
            <span class="dl-kv__value">{{ case.id || 'N/A' }}</span>
          </div>
          <div class="dl-kv__item">
            <span class="dl-kv__key">Patient name</span>
            <span class="dl-kv__value">{{ getPatientName() }}</span>
          </div>
          <div class="dl-kv__item">
            <span class="dl-kv__key">Patient phone</span>
            <span class="dl-kv__value">{{ getPatientPhone() }}</span>
          </div>
          <div class="dl-kv__item">
            <span class="dl-kv__key">Status</span>
            <span class="dl-kv__value">
              <span class="case-status" [class]="getCaseStatusClass(case.status || '')">
                {{ case.status || 'Unknown' }}
              </span>
            </span>
          </div>
          <div class="dl-kv__item">
            <span class="dl-kv__key">Created at</span>
            <span class="dl-kv__value">{{ case.createdAt ? (case.createdAt | date:'medium') : 'N/A' }}</span>
          </div>
        </div>
      </div>
    </div>

    <div class="dl-card">
      <div class="dl-card__header">
        <h2 class="dl-card__title">Partners</h2>
      </div>
      <div class="dl-card__body">
        <div class="dl-kv">
          <div class="dl-kv__item">
            <span class="dl-kv__key">Doctor</span>
            <span class="dl-kv__value">
              <a *ngIf="case.doctor?.id" [routerLink]="['/partners', case.doctor?.id]">{{ getDoctorName() }}</a>
              <span *ngIf="!case.doctor?.id">{{ getDoctorName() }}</span>
              <span class="case-partner-email" *ngIf="case.doctor?.contact?.email">
                {{ case.doctor?.contact?.email }}
              </span>
            </span>
          </div>
          <div class="dl-kv__item">
            <span class="dl-kv__key">Tech</span>
            <span class="dl-kv__value">
              <a *ngIf="case.tech?.id" [routerLink]="['/partners', case.tech?.id]">{{ getTechName() }}</a>
              <span *ngIf="!case.tech?.id">{{ getTechName() }}</span>
              <span class="case-partner-email" *ngIf="case.tech?.contact?.email">
                {{ case.tech?.contact?.email }}
              </span>
            </span>
          </div>
        </div>
      </div>
    </div>

    <div class="dl-card">
      <div class="dl-card__header">
        <h2 class="dl-card__title">Timeline</h2>
      </div>

      <app-empty-state
        *ngIf="timelineItems.length === 0"
        icon="timeline"
        title="Nothing has happened yet">
        No events or shipments have been recorded against this case.
      </app-empty-state>

      <div class="dl-card__body" *ngIf="timelineItems.length > 0">
        <ol class="case-timeline">
          <li *ngFor="let item of timelineItems" class="case-timeline__item">
            <span class="case-timeline__marker" [ngClass]="'is-' + item.type">
              <mat-icon>{{ getTimelineItemIcon(item.type) }}</mat-icon>
            </span>

            <div class="case-timeline__body">
              <div class="case-timeline__head">
                <h3 class="case-timeline__title">{{ item.title }}</h3>
                <span class="case-status" [class]="getTimelineItemStatusClass(item.status)">
                  {{ item.status }}
                </span>
              </div>

              <p class="case-timeline__description">{{ item.description }}</p>

              <p class="case-timeline__meta">
                {{ item.createdAt ? (item.createdAt | date:'medium') : 'N/A' }}
                <span class="case-timeline__type">{{ item.type }}</span>
              </p>

              <!-- Shipments carry addresses and dates that a plain event does not. -->
              <div class="dl-kv case-timeline__shipment" *ngIf="isShipment(item)">
                <div class="dl-kv__item">
                  <span class="dl-kv__key">Order number</span>
                  <span class="dl-kv__value">{{ getShipmentData(item).orderNumber || 'N/A' }}</span>
                </div>
                <div class="dl-kv__item">
                  <span class="dl-kv__key">Type</span>
                  <span class="dl-kv__value">{{ getShipmentData(item).type || 'N/A' }}</span>
                </div>
                <div class="dl-kv__item">
                  <span class="dl-kv__key">From</span>
                  <span class="dl-kv__value">{{ getShipmentData(item).from ? (getShipmentData(item).from!.street + ', ' + getShipmentData(item).from!.city + ', ' + getShipmentData(item).from!.state) : 'N/A' }}</span>
                </div>
                <div class="dl-kv__item">
                  <span class="dl-kv__key">To</span>
                  <span class="dl-kv__value">{{ getShipmentData(item).to ? (getShipmentData(item).to!.street + ', ' + getShipmentData(item).to!.city + ', ' + getShipmentData(item).to!.state) : 'N/A' }}</span>
                </div>
                <div class="dl-kv__item" *ngIf="getShipmentData(item).eta">
                  <span class="dl-kv__key">Estimated delivery</span>
                  <span class="dl-kv__value">{{ getShipmentData(item).eta | date:'medium' }}</span>
                </div>
                <div class="dl-kv__item" *ngIf="getShipmentData(item).deliveredAt">
                  <span class="dl-kv__key">Delivered at</span>
                  <span class="dl-kv__value">{{ getShipmentData(item).deliveredAt | date:'medium' }}</span>
                </div>
                <div class="dl-kv__item" *ngIf="getShipmentData(item).courier">
                  <span class="dl-kv__key">Courier</span>
                  <span class="dl-kv__value">{{ getShipmentData(item).courier!.contact?.name || 'N/A' }}</span>
                </div>
              </div>
            </div>
          </li>
        </ol>
      </div>
    </div>
  </ng-container>
</div>

<app-confirm-dialog
  [isOpen]="confirmingDelete"
  title="Delete case"
  [message]="'Case #' + (case?.id || '') + ' will be removed permanently. This cannot be undone.'"
  confirmLabel="Delete case"
  confirmClass="dl-btn--danger"
  [busy]="deleting"
  (confirm)="confirmDelete()"
  (cancel)="confirmingDelete = false">
</app-confirm-dialog>
